import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Upload, FileText, Clock, CheckCircle, AlertTriangle, Eye, Download, ChevronRight, Briefcase, BarChart3, FolderOpen } from 'lucide-react';
import { usePortalStore } from '../store';

const statusColors = {
  pending: { bg: 'bg-yellow-500/10', text: 'text-yellow-400', border: 'border-yellow-500/20' },
  reviewed: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20' },
  approved: { bg: 'bg-green-500/10', text: 'text-green-400', border: 'border-green-500/20' },
  flagged: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20' },
};

const matterStatusColors = {
  active: { bg: 'bg-green-500/10', text: 'text-green-400' },
  pending: { bg: 'bg-yellow-500/10', text: 'text-yellow-400' },
  review: { bg: 'bg-blue-500/10', text: 'text-blue-400' },
  closed: { bg: 'bg-gray-500/10', text: 'text-gray-400' },
};

export default function PortalPage() {
  const { documents, matters, activeTab, setActiveTab, addDocument, uploadProgress, setUploadProgress } = usePortalStore();
  const [isDragging, setIsDragging] = useState(false);
  const [selectedMatterId, setSelectedMatterId] = useState<string | null>(null);
  const [loginView, setLoginView] = useState(true);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (loginEmail && loginPassword) {
      setLoginView(false);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      simulateUpload(files[0].name);
    }
  }, []);

  const simulateUpload = (filename: string) => {
    setUploadProgress(0);
    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev === null) return 0;
        if (prev >= 100) {
          clearInterval(interval);
          addDocument({
            id: Date.now().toString(),
            name: filename || 'Uploaded Document.pdf',
            type: 'PDF',
            uploadDate: new Date().toISOString().split('T')[0],
            status: 'pending',
            size: '1.2 MB',
          });
          setTimeout(() => setUploadProgress(null), 500);
          return 100;
        }
        return prev + 15;
      });
    }, 200);
  };

  if (loginView) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="min-h-screen flex items-center justify-center">
        <section className="pt-20 pb-20 relative w-full">
          <div className="absolute inset-0 bg-gradient-to-br from-[#050d1a] via-[#0a1628] to-[#0f2240]" />
          <div className="absolute top-20 right-20 w-[500px] h-[500px] bg-[#c9a84c]/5 rounded-full blur-[150px]" />
          <div className="max-w-md mx-auto px-4 relative z-10">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#c9a84c] to-[#b08e2e] flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-[#0a1628]" />
              </div>
              <h1 className="text-3xl font-bold text-[#e8e4dc] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                Client <span className="text-[#c9a84c]">Portal</span>
              </h1>
              <p className="text-sm text-[#e8e4dc]/50">Sign in to access your secure client portal</p>
            </div>
            <form onSubmit={handleLogin} className="p-8 rounded-2xl bg-gradient-to-b from-[#0f2240]/60 to-[#0a1628]/60 border border-[#c9a84c]/15 space-y-5">
              <div>
                <label className="block text-xs text-[#c9a84c] font-semibold uppercase tracking-wider mb-2">Email Address</label>
                <input
                  type="email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-[#0a1628]/60 border border-[#c9a84c]/15 rounded-xl text-sm text-[#e8e4dc] focus:border-[#c9a84c]/40 focus:outline-none transition-colors placeholder:text-[#e8e4dc]/20"
                  placeholder="client@example.com"
                />
              </div>
              <div>
                <label className="block text-xs text-[#c9a84c] font-semibold uppercase tracking-wider mb-2">Password</label>
                <input
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-[#0a1628]/60 border border-[#c9a84c]/15 rounded-xl text-sm text-[#e8e4dc] focus:border-[#c9a84c]/40 focus:outline-none transition-colors placeholder:text-[#e8e4dc]/20"
                  placeholder="••••••••"
                />
              </div>
              <button
                type="submit"
                className="w-full py-3 bg-gradient-to-r from-[#c9a84c] to-[#b08e2e] text-[#0a1628] font-semibold rounded-xl hover:shadow-lg hover:shadow-[#c9a84c]/20 transition-all duration-300 text-sm"
              >
                Sign In Securely
              </button>
              <div className="flex items-center justify-center gap-2 text-xs text-[#e8e4dc]/30 pt-2">
                <Shield className="w-3 h-3" />
                <span>256-bit AES encrypted connection</span>
              </div>
            </form>
          </div>
        </section>
      </motion.div>
    );
  }

  const selectedMatter = matters.find(m => m.id === selectedMatterId);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
      {/* Hero */}
      <section className="pt-32 pb-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#050d1a] via-[#0a1628] to-[#0f2240]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex items-center justify-between">
            <div>
              <div className="gold-line mb-4" />
              <h1 className="text-3xl md:text-4xl font-bold text-[#e8e4dc]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Welcome, <span className="text-[#c9a84c]">Client</span>
              </h1>
            </div>
            <button
              onClick={() => setLoginView(true)}
              className="text-xs text-[#e8e4dc]/40 hover:text-[#c9a84c] transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
      </section>

      {/* Tab nav */}
      <section className="border-b border-[#c9a84c]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1">
            {[
              { id: 'documents' as const, label: 'Documents', icon: FolderOpen },
              { id: 'matters' as const, label: 'Legal Matters', icon: Briefcase },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setSelectedMatterId(null); }}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-all ${
                  activeTab === tab.id
                    ? 'border-[#c9a84c] text-[#c9a84c]'
                    : 'border-transparent text-[#e8e4dc]/40 hover:text-[#e8e4dc]/70'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Upload Area */}
              <div className="lg:col-span-1">
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`p-8 rounded-2xl border-2 border-dashed transition-all duration-300 text-center ${
                    isDragging
                      ? 'border-[#c9a84c]/60 bg-[#c9a84c]/10'
                      : 'border-[#c9a84c]/20 bg-[#0f2240]/30 hover:border-[#c9a84c]/30'
                  }`}
                >
                  <Upload className="w-10 h-10 text-[#c9a84c]/60 mx-auto mb-4" />
                  <p className="text-sm font-medium text-[#e8e4dc] mb-2">Drag & drop files here</p>
                  <p className="text-xs text-[#e8e4dc]/40 mb-4">or click to browse</p>
                  <input
                    type="file"
                    id="file-upload"
                    className="hidden"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        simulateUpload(e.target.files[0].name);
                      }
                    }}
                  />
                  <label
                    htmlFor="file-upload"
                    className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#c9a84c]/15 border border-[#c9a84c]/30 text-[#c9a84c] rounded-xl text-xs font-medium cursor-pointer hover:bg-[#c9a84c]/25 transition-all"
                  >
                    <Upload className="w-3 h-3" /> Select Files
                  </label>
                  <p className="text-[10px] text-[#e8e4dc]/25 mt-4">PDF, DOC, DOCX, XLS, XLSX, JPG, PNG — Max 25MB</p>
                </div>

                {/* Upload Progress */}
                <AnimatePresence>
                  {uploadProgress !== null && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="mt-4 p-4 rounded-xl bg-[#0f2240]/40 border border-[#c9a84c]/10"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-[#e8e4dc]/60">Uploading...</span>
                        <span className="text-xs text-[#c9a84c] font-medium">{uploadProgress}%</span>
                      </div>
                      <div className="w-full h-1.5 bg-[#0a1628]/60 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-[#c9a84c] to-[#b08e2e] rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-3 mt-6">
                  <div className="p-4 rounded-xl bg-[#0f2240]/30 border border-[#c9a84c]/10 text-center">
                    <div className="text-2xl font-bold text-[#e8e4dc]" style={{ fontFamily: 'Playfair Display, serif' }}>{documents.length}</div>
                    <div className="text-xs text-[#e8e4dc]/40">Documents</div>
                  </div>
                  <div className="p-4 rounded-xl bg-[#0f2240]/30 border border-[#c9a84c]/10 text-center">
                    <div className="text-2xl font-bold text-[#c9a84c]" style={{ fontFamily: 'Playfair Display, serif' }}>{documents.filter(d => d.status === 'approved').length}</div>
                    <div className="text-xs text-[#e8e4dc]/40">Approved</div>
                  </div>
                </div>
              </div>

              {/* Document List */}
              <div className="lg:col-span-2">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-bold text-[#e8e4dc]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Uploaded Documents
                  </h3>
                  <span className="text-xs text-[#e8e4dc]/40">{documents.length} files</span>
                </div>
                <div className="space-y-3">
                  {documents.map((doc) => {
                    const sc = statusColors[doc.status];
                    return (
                      <motion.div
                        key={doc.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-center gap-4 p-4 rounded-xl bg-[#0f2240]/30 border border-[#c9a84c]/10 hover:border-[#c9a84c]/15 transition-all"
                      >
                        <div className="w-10 h-10 rounded-lg bg-[#c9a84c]/10 flex items-center justify-center shrink-0">
                          <FileText className="w-5 h-5 text-[#c9a84c]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#e8e4dc] truncate">{doc.name}</p>
                          <p className="text-xs text-[#e8e4dc]/40 mt-0.5">{doc.size} • {doc.type} • Uploaded {doc.uploadDate}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${sc.bg} ${sc.text} ${sc.border}`}>
                          {doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                        </span>
                        <div className="flex items-center gap-2">
                          <button className="p-2 rounded-lg hover:bg-[#c9a84c]/10 transition-colors">
                            <Eye className="w-4 h-4 text-[#e8e4dc]/40 hover:text-[#c9a84c]" />
                          </button>
                          <button className="p-2 rounded-lg hover:bg-[#c9a84c]/10 transition-colors">
                            <Download className="w-4 h-4 text-[#e8e4dc]/40 hover:text-[#c9a84c]" />
                          </button>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Matters Tab */}
          {activeTab === 'matters' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Matter List */}
              <div className={`${selectedMatter ? 'hidden lg:block' : ''} lg:col-span-1 space-y-3`}>
                <h3 className="text-lg font-bold text-[#e8e4dc] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Active Matters
                </h3>
                {matters.map((matter) => {
                  const ms = matterStatusColors[matter.status];
                  return (
                    <button
                      key={matter.id}
                      onClick={() => setSelectedMatterId(matter.id)}
                      className={`w-full text-left p-4 rounded-xl border transition-all ${
                        selectedMatterId === matter.id
                          ? 'bg-[#c9a84c]/10 border-[#c9a84c]/30'
                          : 'bg-[#0f2240]/30 border-[#c9a84c]/10 hover:border-[#c9a84c]/15'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <span className="text-sm font-medium text-[#e8e4dc] leading-tight">{matter.title}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${ms.bg} ${ms.text} shrink-0 ml-2`}>
                          {matter.status}
                        </span>
                      </div>
                      <p className="text-xs text-[#e8e4dc]/40">{matter.caseNumber}</p>
                      <div className="mt-3">
                        <div className="w-full h-1 bg-[#0a1628]/60 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-[#c9a84c] to-[#b08e2e] rounded-full"
                            style={{ width: `${matter.progress}%` }}
                          />
                        </div>
                        <div className="flex justify-between mt-1">
                          <span className="text-[10px] text-[#e8e4dc]/30">Progress</span>
                          <span className="text-[10px] text-[#c9a84c]/60">{matter.progress}%</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Matter Detail */}
              <div className={`${selectedMatter ? '' : 'hidden lg:block'} lg:col-span-2`}>
                {selectedMatter ? (
                  <motion.div
                    key={selectedMatter.id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="p-8 rounded-2xl bg-gradient-to-b from-[#0f2240]/50 to-[#0a1628]/50 border border-[#c9a84c]/10"
                  >
                    <div className="flex items-start justify-between mb-6">
                      <div>
                        <button
                          onClick={() => setSelectedMatterId(null)}
                          className="lg:hidden text-xs text-[#c9a84c]/70 hover:text-[#c9a84c] mb-3 block"
                        >
                          ← Back to list
                        </button>
                        <h3 className="text-2xl font-bold text-[#e8e4dc] mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                          {selectedMatter.title}
                        </h3>
                        <p className="text-sm text-[#e8e4dc]/40">Case No. {selectedMatter.caseNumber}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${matterStatusColors[selectedMatter.status].bg} ${matterStatusColors[selectedMatter.status].text}`}>
                        {selectedMatter.status.charAt(0).toUpperCase() + selectedMatter.status.slice(1)}
                      </span>
                    </div>

                    {/* Progress */}
                    <div className="mb-8">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-[#e8e4dc]/50">Case Progress</span>
                        <span className="text-sm font-bold text-[#c9a84c]">{selectedMatter.progress}%</span>
                      </div>
                      <div className="w-full h-3 bg-[#0a1628]/60 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${selectedMatter.progress}%` }}
                          transition={{ duration: 1, delay: 0.3 }}
                          className="h-full bg-gradient-to-r from-[#c9a84c] to-[#b08e2e] rounded-full"
                        />
                      </div>
                    </div>

                    {/* Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                      <div className="p-4 rounded-xl bg-[#0a1628]/40">
                        <div className="flex items-center gap-2 mb-2">
                          <Briefcase className="w-4 h-4 text-[#c9a84c]/60" />
                          <span className="text-xs text-[#e8e4dc]/40 uppercase tracking-wider">Attorney</span>
                        </div>
                        <p className="text-sm font-medium text-[#e8e4dc]">{selectedMatter.attorney}</p>
                      </div>
                      <div className="p-4 rounded-xl bg-[#0a1628]/40">
                        <div className="flex items-center gap-2 mb-2">
                          <Clock className="w-4 h-4 text-[#c9a84c]/60" />
                          <span className="text-xs text-[#e8e4dc]/40 uppercase tracking-wider">Next Deadline</span>
                        </div>
                        <p className="text-sm font-medium text-[#e8e4dc]">{new Date(selectedMatter.nextDeadline + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                      </div>
                      <div className="p-4 rounded-xl bg-[#0a1628]/40">
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle className="w-4 h-4 text-[#c9a84c]/60" />
                          <span className="text-xs text-[#e8e4dc]/40 uppercase tracking-wider">Last Update</span>
                        </div>
                        <p className="text-sm font-medium text-[#e8e4dc]">{new Date(selectedMatter.lastUpdate + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                      </div>
                      <div className="p-4 rounded-xl bg-[#0a1628]/40">
                        <div className="flex items-center gap-2 mb-2">
                          <BarChart3 className="w-4 h-4 text-[#c9a84c]/60" />
                          <span className="text-xs text-[#e8e4dc]/40 uppercase tracking-wider">Status</span>
                        </div>
                        <p className="text-sm font-medium text-[#e8e4dc] capitalize">{selectedMatter.status}</p>
                      </div>
                    </div>

                    {/* Description */}
                    <div className="p-4 rounded-xl bg-[#0a1628]/40 mb-6">
                      <span className="text-xs text-[#e8e4dc]/40 uppercase tracking-wider block mb-2">Description</span>
                      <p className="text-sm text-[#e8e4dc]/70 leading-relaxed">{selectedMatter.description}</p>
                    </div>

                    {/* Timeline */}
                    <div>
                      <h4 className="text-sm font-semibold text-[#e8e4dc] mb-4">Recent Activity</h4>
                      <div className="space-y-4">
                        {[
                          { date: 'Jul 9, 2026', event: 'Document submitted for attorney review', type: 'upload' },
                          { date: 'Jul 5, 2026', event: 'Case status updated to review', type: 'status' },
                          { date: 'Jun 28, 2026', event: 'New correspondence received', type: 'message' },
                          { date: 'Jun 20, 2026', event: 'Court filing completed', type: 'filing' },
                        ].map((item, idx) => (
                          <div key={idx} className="flex gap-4">
                            <div className="flex flex-col items-center">
                              <div className="w-2 h-2 rounded-full bg-[#c9a84c]/60" />
                              {idx < 3 && <div className="w-px h-full bg-[#c9a84c]/10" />}
                            </div>
                            <div className="pb-4">
                              <p className="text-xs text-[#e8e4dc]/30">{item.date}</p>
                              <p className="text-sm text-[#e8e4dc]/70">{item.event}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-64 text-center">
                    <Briefcase className="w-12 h-12 text-[#c9a84c]/20 mb-4" />
                    <p className="text-sm text-[#e8e4dc]/40">Select a matter to view details</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
    </motion.div>
  );
}
